import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CatalogItem } from '@lumos/contracts';
import { tinyImage, washImage } from './images.js';
import { TitleStage, type StageSource } from './TitleStage.js';

/**
 * Сколько карточка должна пробыть под курсором или в фокусе, прежде чем
 * подложка раздела сменится на её кадр, — та же пауза, что у верха главной:
 * проход курсором через сетку не должен перебирать фон на каждой карточке.
 */
export const WASH_FOCUS_MS = 250;

/**
 * Какой тайтл стоит подложкой списка: карточка под курсором или в фокусе
 * (после паузы), а пока зритель ничего не трогал — первая в списке.
 */
export function useWashItem(items: readonly CatalogItem[]): {
  washItem: CatalogItem | null;
  onFocusItem: (item: CatalogItem) => void;
  /** Курсор или фокус ушли с карточки раньше паузы — подложка остаётся прежней. */
  onLeaveItem: () => void;
} {
  const [focused, setFocused] = useState<CatalogItem | null>(null);
  const timer = useRef<number | null>(null);
  const onFocusItem = useCallback((item: CatalogItem): void => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setFocused(item);
    }, WASH_FOCUS_MS);
  }, []);
  const onLeaveItem = useCallback((): void => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  }, []);
  useEffect(() => onLeaveItem, [onLeaveItem]);
  // Карточки, на которую наводили, в списке больше нет (сменили жанр, новый
  // запрос) — подложка возвращается к первой.
  const current = focused !== null && items.some((item) => item.id === focused.id) ? focused : null;
  return { washItem: current ?? items[0] ?? null, onFocusItem, onLeaveItem };
}

/**
 * Подложка раздела («Фильмы», «Сериалы», «Избранное», «История», поиск,
 * фильмография): мягкий расфокус кадра тайтла в правом верхнем углу, к левому
 * и нижнему краю уходит в цвет страницы — как `WashShape.CORNER` в приложении.
 *
 * Механизм тот же, что у фона страницы тайтла: крупная копия кадра (нет кадра
 * — постера), размытая один раз на маленьком холсте (`soft-image.ts`, вид
 * `light`), один неподвижный слой без фильтров; смена тайтла — растворением
 * (`TitleStage`). Слой прилипает к верху экрана и с прокруткой не считается.
 */
export function SectionWash({ item }: { item: CatalogItem | null }): React.JSX.Element | null {
  const source = useMemo<StageSource | null>(() => {
    if (item === null) return null;
    const wash = washImage(item.backdrop?.url, item.poster?.url);
    if (wash === null) return null;
    return { key: item.id, art: null, tiny: tinyImage(item.poster?.url ?? item.backdrop?.url ?? null), wash };
  }, [item]);
  // Пока показывать нечего, слоя нет вовсе; появился тайтл — сцена живёт дальше
  // и при пустом списке просто стоит (`source === null`).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (source !== null) setMounted(true);
  }, [source]);
  if (!mounted) return null;
  return <TitleStage source={source} className="section-wash" soft />;
}
